import React, { useState, useEffect } from 'react';
import InteractiveSVGMap from './InteractiveSVGMap';

const AreaActivityMap = ({ selectedRegion, onRegionClick }) => {
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [fadeIn, setFadeIn] = useState(true);

  // Handle smooth transitions between views
  useEffect(() => {
    setIsTransitioning(true);
    setFadeIn(false);
    
    const timer = setTimeout(() => {
      setFadeIn(true);
      setIsTransitioning(false);
    }, 150);

    return () => clearTimeout(timer);
  }, [selectedRegion.level, selectedRegion.id]);

  // Handle continent click from interactive SVG
  const handleContinentClick = (continentId, continentName) => {
    onRegionClick({
      level: 'continent',
      id: continentId,
      name: continentName,
      path: ['Global', continentName]
    });
  };

  // Handle country click
  const handleCountryClick = (countryId, countryName, continentId, continentName, event) => {
    event.stopPropagation();
    console.log('Country clicked:', countryId, countryName);
    onRegionClick({
      level: 'country',
      id: countryId,
      name: countryName,
      path: ['Global', continentName, countryName]
    });
  };

  // Handle state/province click
  const handleStateClick = (stateId, stateName, countryId, countryName, event) => {
    if (event) event.stopPropagation();
    console.log('State/Province clicked:', stateId, stateName);
    onRegionClick({
      level: 'state',
      id: stateId,
      name: stateName,
      path: ['Global', 'North America', countryName, stateName]
    });
  };

  // Determine what to show based on current selection
  const getDisplayMode = () => {
    if (selectedRegion.level === 'global') return 'continents';
    if (selectedRegion.level === 'continent') return 'countries';
    if (selectedRegion.level === 'country') return 'states';
    if (selectedRegion.level === 'state') return 'states'; // Keep showing 'states' view for individual states
    return 'continents';
  };

  const displayMode = getDisplayMode();

  return (
    <div style={{
      width: '100%',
      height: '100%',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      background: 'radial-gradient(ellipse at center, rgba(0, 255, 255, 0.05) 0%, rgba(10, 14, 39, 0.9) 70%)',
      opacity: fadeIn ? 1 : 0,
      transition: 'opacity 0.3s ease-in-out'
    }}>
      {/* Neon grid background pattern */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `
          linear-gradient(rgba(0, 255, 255, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0, 255, 255, 0.03) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
        opacity: 0.3,
        pointerEvents: 'none'
      }}></div>

      {/* Global View - Interactive Neon SVG Map */}
      {displayMode === 'continents' && (
        <div style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          {/* Interactive SVG Map with neon styling - serves as both visual and interactive layer */}
          <InteractiveSVGMap onContinentClick={handleContinentClick} />
        </div>
      )}

      {/* Country View - North America zoom */}
      {displayMode === 'countries' && selectedRegion.id === 'north_america' && (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          gap: '15px'
        }}>
          <div style={{ flexShrink: 0 }}>
            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#00FFFF',
              filter: 'drop-shadow(0 0 8px rgba(0, 255, 255, 0.9))',
              textAlign: 'center'
            }}>
              North America
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%',
            maxWidth: '350px'
          }}>
            {/* USA Card */}
            <div
              style={{
                width: '100%',
                minHeight: '70px',
                background: selectedRegion.name === 'United States' ? 'rgba(255, 0, 255, 0.3)' : hoveredRegion === 'usa' ? 'rgba(255, 0, 255, 0.2)' : 'rgba(255, 0, 255, 0.08)',
                border: `3px solid ${hoveredRegion === 'usa' || selectedRegion.name === 'United States' ? '#FF00FF' : 'rgba(255, 0, 255, 0.5)'}`,
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: hoveredRegion === 'usa' ? 'scale(1.02) translateY(-2px)' : 'scale(1)',
                filter: selectedRegion.name === 'United States' ? 'drop-shadow(0 0 15px rgba(255, 0, 255, 0.8))' : hoveredRegion === 'usa' ? 'drop-shadow(0 0 10px rgba(255, 0, 255, 0.6))' : 'none',
                padding: '12px'
              }}
              onMouseEnter={() => setHoveredRegion('usa')}
              onMouseLeave={() => setHoveredRegion(null)}
              onClick={(e) => handleCountryClick('usa', 'United States', 'north_america', 'North America', e)}
            >
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#FF00FF', marginBottom: '5px' }}>
                USA
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                Click to view states
              </div>
            </div>

            {/* Canada Card */}
            <div
              style={{
                width: '100%',
                minHeight: '70px',
                background: selectedRegion.name === 'Canada' ? 'rgba(255, 0, 255, 0.3)' : hoveredRegion === 'canada' ? 'rgba(255, 0, 255, 0.2)' : 'rgba(255, 0, 255, 0.08)',
                border: `3px solid ${hoveredRegion === 'canada' || selectedRegion.name === 'Canada' ? '#FF00FF' : 'rgba(255, 0, 255, 0.5)'}`,
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: hoveredRegion === 'canada' ? 'scale(1.02) translateY(-2px)' : 'scale(1)',
                filter: selectedRegion.name === 'Canada' ? 'drop-shadow(0 0 15px rgba(255, 0, 255, 0.8))' : hoveredRegion === 'canada' ? 'drop-shadow(0 0 10px rgba(255, 0, 255, 0.6))' : 'none',
                padding: '12px'
              }}
              onMouseEnter={() => setHoveredRegion('canada')}
              onMouseLeave={() => setHoveredRegion(null)}
              onClick={(e) => handleCountryClick('canada', 'Canada', 'north_america', 'North America', e)}
            >
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#FF00FF', marginBottom: '5px' }}>
                Canada
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                Click to view provinces
              </div>
            </div>

            {/* Mexico Card */}
            <div
              style={{
                width: '100%',
                minHeight: '70px',
                background: selectedRegion.name === 'Mexico' ? 'rgba(255, 0, 255, 0.3)' : hoveredRegion === 'mexico' ? 'rgba(255, 0, 255, 0.2)' : 'rgba(255, 0, 255, 0.08)',
                border: `3px solid ${hoveredRegion === 'mexico' || selectedRegion.name === 'Mexico' ? '#FF00FF' : 'rgba(255, 0, 255, 0.5)'}`,
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: hoveredRegion === 'mexico' ? 'scale(1.02) translateY(-2px)' : 'scale(1)',
                filter: selectedRegion.name === 'Mexico' ? 'drop-shadow(0 0 15px rgba(255, 0, 255, 0.8))' : hoveredRegion === 'mexico' ? 'drop-shadow(0 0 10px rgba(255, 0, 255, 0.6))' : 'none',
                padding: '12px'
              }}
              onMouseEnter={() => setHoveredRegion('mexico')}
              onMouseLeave={() => setHoveredRegion(null)}
              onClick={(e) => handleCountryClick('mexico', 'Mexico', 'north_america', 'North America', e)}
            >
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#FF00FF', marginBottom: '5px' }}>
                Mexico
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                Click to view data
              </div>
            </div>

            {/* Greenland Card */}
            <div
              style={{
                width: '100%',
                minHeight: '70px',
                background: selectedRegion.name === 'Greenland' ? 'rgba(255, 0, 255, 0.3)' : hoveredRegion === 'greenland' ? 'rgba(255, 0, 255, 0.2)' : 'rgba(255, 0, 255, 0.08)',
                border: `3px solid ${hoveredRegion === 'greenland' || selectedRegion.name === 'Greenland' ? '#FF00FF' : 'rgba(255, 0, 255, 0.5)'}`,
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: hoveredRegion === 'greenland' ? 'scale(1.02) translateY(-2px)' : 'scale(1)',
                filter: selectedRegion.name === 'Greenland' ? 'drop-shadow(0 0 15px rgba(255, 0, 255, 0.8))' : hoveredRegion === 'greenland' ? 'drop-shadow(0 0 10px rgba(255, 0, 255, 0.6))' : 'none',
                padding: '12px'
              }}
              onMouseEnter={() => setHoveredRegion('greenland')}
              onMouseLeave={() => setHoveredRegion(null)}
              onClick={(e) => handleCountryClick('greenland', 'Greenland', 'north_america', 'North America', e)}
            >
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#FF00FF', marginBottom: '5px' }}>
                Greenland
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                Click to view data
              </div>
            </div>
          </div>
        </div>
      )}

      {/* State View - USA States */}
      {displayMode === 'states' && selectedRegion.id === 'usa' && (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: '15px',
          overflowY: 'auto',
          gap: '15px'
        }}>
          <div style={{ flexShrink: 0 }}>
            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#00FFFF',
              filter: 'drop-shadow(0 0 8px rgba(0, 255, 255, 0.9))',
              textAlign: 'center'
            }}>
              United States
            </div>
            <div style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', marginTop: '5px' }}>
              All 50 States
            </div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '10px',
            paddingBottom: '15px'
          }}>
            {[
              'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 
              'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia',
              'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
              'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland',
              'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri',
              'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
              'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
              'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
              'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
              'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
            ].map((state) => (
              <div
                key={state}
                style={{
                  minHeight: '60px',
                  background: hoveredRegion === state ? 'rgba(0, 255, 255, 0.25)' : 'rgba(0, 255, 255, 0.1)',
                  border: `2px solid ${hoveredRegion === state ? '#00FFFF' : 'rgba(0, 255, 255, 0.5)'}`,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  filter: hoveredRegion === state ? 'drop-shadow(0 0 10px rgba(0, 255, 255, 0.8))' : 'none',
                  padding: '8px'
                }}
                onMouseEnter={() => setHoveredRegion(state)}
                onMouseLeave={() => setHoveredRegion(null)}
                onClick={(e) => handleStateClick(
                  state.toLowerCase().replace(/\s+/g, '_'),
                  state,
                  'usa',
                  'United States',
                  e
                )}
              >
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#00FFFF',
                  filter: 'drop-shadow(0 0 3px rgba(0, 255, 255, 0.8))',
                  textAlign: 'center',
                  lineHeight: '1.2'
                }}>
                  {state}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* State View - Canada Provinces */}
      {displayMode === 'states' && selectedRegion.id === 'canada' && (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: '15px',
          overflowY: 'auto',
          gap: '15px'
        }}>
          <div style={{ flexShrink: 0 }}>
            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#00FFFF',
              filter: 'drop-shadow(0 0 8px rgba(0, 255, 255, 0.9))',
              textAlign: 'center'
            }}>
              Canada
            </div>
            <div style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', marginTop: '5px' }}>
              All Provinces & Territories
            </div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '10px',
            paddingBottom: '15px'
          }}>
            {[
              'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 
              'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia', 
              'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 
              'Saskatchewan', 'Yukon'
            ].map((province) => (
              <div
                key={province}
                style={{
                  minHeight: '60px',
                  background: hoveredRegion === province ? 'rgba(0, 255, 255, 0.25)' : 'rgba(0, 255, 255, 0.1)',
                  border: `2px solid ${hoveredRegion === province ? '#00FFFF' : 'rgba(0, 255, 255, 0.5)'}`,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  filter: hoveredRegion === province ? 'drop-shadow(0 0 10px rgba(0, 255, 255, 0.8))' : 'none',
                  padding: '8px'
                }}
                onMouseEnter={() => setHoveredRegion(province)}
                onMouseLeave={() => setHoveredRegion(null)}
                onClick={(e) => handleStateClick(
                  province.toLowerCase().replace(/\s+/g, '_'),
                  province,
                  'canada',
                  'Canada',
                  e
                )}
              >
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#00FFFF',
                  filter: 'drop-shadow(0 0 3px rgba(0, 255, 255, 0.8))',
                  textAlign: 'center',
                  lineHeight: '1.2'
                }}>
                  {province}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Country View - Mexico */}
      {displayMode === 'states' && selectedRegion.id === 'mexico' && (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px',
          padding: '20px'
        }}>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#00FFFF',
            filter: 'drop-shadow(0 0 12px rgba(0, 255, 255, 0.9))',
            textAlign: 'center'
          }}>
            Mexico
          </div>
          <div style={{
            fontSize: '16px',
            color: '#94a3b8',
            textAlign: 'center',
            maxWidth: '500px',
            lineHeight: '1.6'
          }}>
            Viewing country-level data. Check the panels on the right for live music activity, events, and trending tracks in Mexico.
          </div>
          <div style={{
            fontSize: '14px',
            color: '#00FFFF',
            textAlign: 'center',
            marginTop: '10px',
            padding: '12px 24px',
            background: 'rgba(0, 255, 255, 0.1)',
            border: '2px solid rgba(0, 255, 255, 0.3)',
            borderRadius: '8px',
            filter: 'drop-shadow(0 0 5px rgba(0, 255, 255, 0.4))'
          }}>
            State-level drill-down coming soon
          </div>
        </div>
      )}

      {/* Country View - Greenland */}
      {displayMode === 'states' && selectedRegion.id === 'greenland' && (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px',
          padding: '20px'
        }}>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#00FFFF',
            filter: 'drop-shadow(0 0 12px rgba(0, 255, 255, 0.9))',
            textAlign: 'center'
          }}>
            Greenland
          </div>
          <div style={{
            fontSize: '16px',
            color: '#94a3b8',
            textAlign: 'center',
            maxWidth: '500px',
            lineHeight: '1.6'
          }}>
            Viewing country-level data. Check the panels on the right for live music activity, events, and trending tracks in Greenland.
          </div>
          <div style={{
            fontSize: '14px',
            color: '#00FFFF',
            textAlign: 'center',
            marginTop: '10px',
            padding: '12px 24px',
            background: 'rgba(0, 255, 255, 0.1)',
            border: '2px solid rgba(0, 255, 255, 0.3)',
            borderRadius: '8px',
            filter: 'drop-shadow(0 0 5px rgba(0, 255, 255, 0.4))'
          }}>
            Territory-level drill-down coming soon
          </div>
        </div>
      )}

      {/* Individual State/Province View - Generic Placeholder */}
      {displayMode === 'states' && 
       selectedRegion.id !== 'usa' && 
       selectedRegion.id !== 'canada' && 
       selectedRegion.id !== 'mexico' && 
       selectedRegion.id !== 'greenland' && (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px',
          padding: '20px'
        }}>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#00FFFF',
            filter: 'drop-shadow(0 0 12px rgba(0, 255, 255, 0.9))',
            textAlign: 'center'
          }}>
            {selectedRegion.name}
          </div>
          <div style={{
            fontSize: '16px',
            color: '#94a3b8',
            textAlign: 'center',
            maxWidth: '500px',
            lineHeight: '1.6'
          }}>
            Viewing state/province-level data. Check the panels on the right for live music activity, events, and trending tracks in {selectedRegion.name}.
          </div>
          <div style={{
            fontSize: '14px',
            color: '#00FFFF',
            textAlign: 'center',
            marginTop: '10px',
            padding: '12px 24px',
            background: 'rgba(0, 255, 255, 0.1)',
            border: '2px solid rgba(0, 255, 255, 0.3)',
            borderRadius: '8px',
            filter: 'drop-shadow(0 0 5px rgba(0, 255, 255, 0.4))'
          }}>
            City-level drill-down coming soon
          </div>
        </div>
      )}

      {/* Other Continents - Show data at continent level */}
      {displayMode === 'countries' && selectedRegion.id !== 'north_america' && (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px',
          padding: '20px'
        }}>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#00FFFF',
            filter: 'drop-shadow(0 0 12px rgba(0, 255, 255, 0.9))',
            textAlign: 'center'
          }}>
            {selectedRegion.name}
          </div>
          <div style={{
            fontSize: '16px',
            color: '#94a3b8',
            textAlign: 'center',
            maxWidth: '500px',
            lineHeight: '1.6'
          }}>
            Viewing continent-level data. Check the panels on the right for live music activity, events, and trending tracks in {selectedRegion.name}.
          </div>
          <div style={{
            fontSize: '14px',
            color: '#00FFFF',
            textAlign: 'center',
            marginTop: '10px',
            padding: '12px 24px',
            background: 'rgba(0, 255, 255, 0.1)',
            border: '2px solid rgba(0, 255, 255, 0.3)',
            borderRadius: '8px',
            filter: 'drop-shadow(0 0 5px rgba(0, 255, 255, 0.4))'
          }}>
            Country-level drill-down coming soon
          </div>
        </div>
      )}
    </div>
  );
};

export default AreaActivityMap;
